import { useEffect, useState } from 'react'
import { cantidad as formatearCantidad, fechaHora, dinero } from '../api/formato'
import { ventas } from '../api/servicios'
import type { Comprobante } from '../api/tipos'
import './ticket.css'

/**
 * Comprobante para imprimir.
 *
 * En pantalla se muestra en pequenio como confirmacion; al imprimir, el resto
 * de la aplicacion desaparece y queda solo este bloque, ajustado al ancho del
 * rollo termico.
 */
export function Ticket({
    ventaId,
    alCargar,
}: {
    ventaId: number
    /**
     * Se avisa hacia arriba en cuanto llega el comprobante. El puente de
     * impresion necesita el mismo objeto, y pedirlo de nuevo seria una segunda
     * consulta por cada venta para traer exactamente lo que ya esta aqui.
     */
    alCargar?: (comprobante: Comprobante) => void
}) {
    const [datos, setDatos] = useState<Comprobante | null>(null)

    useEffect(() => {
        let vigente = true

        void ventas
            .comprobante(ventaId)
            .then((comprobante) => {
                if (vigente) {
                    setDatos(comprobante)
                    alCargar?.(comprobante)
                }
            })
            .catch(() => {
                // El comprobante es secundario: la venta ya quedo registrada.
            })

        return () => {
            vigente = false
        }
        // alCargar queda fuera a proposito: si el padre lo redefine en cada
        // render, incluirlo volveria a pedir el comprobante en cada uno.
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [ventaId])

    if (!datos) return null

    return (
        <div className="ticket" id="ticket-impresion">
            <div className="ticket-centro">
                <strong>{datos.razonSocial}</strong>
                {datos.rut && <div>RUT {datos.rut}</div>}
                {datos.direccion && <div>{datos.direccion}</div>}
                {datos.telefono && <div>{datos.telefono}</div>}
            </div>

            <hr />

            <div className="ticket-fila">
                <span>{datos.numero}</span>
                <span>{fechaHora(datos.fecha)}</span>
            </div>
            <div className="ticket-fila">
                <span>Atendió</span>
                <span>{datos.cajero}</span>
            </div>
            {datos.cliente && (
                <div className="ticket-fila">
                    <span>Cliente</span>
                    <span>{datos.cliente}</span>
                </div>
            )}

            <hr />

            {datos.lineas.map((linea) => (
                <div key={linea.ventaDetalleId} className="ticket-linea">
                    <div>{linea.descripcion}</div>
                    <div className="ticket-fila">
                        <span>
                            {formatearCantidad(linea.cantidad, linea.unidadMedidaCodigo === 'UND' ? 0 : 3)}
                            {' × '}
                            {dinero(linea.precioUnitario, datos.simboloMoneda)}
                        </span>
                        <span>{dinero(linea.total, datos.simboloMoneda)}</span>
                    </div>
                    {linea.descuento > 0 && (
                        <div className="ticket-fila ticket-tenue">
                            <span>descuento</span>
                            <span>−{dinero(linea.descuento, datos.simboloMoneda)}</span>
                        </div>
                    )}
                </div>
            ))}

            <hr />

            {datos.descuentoTotal > 0 && (
                <>
                    <div className="ticket-fila">
                        <span>Subtotal</span>
                        <span>{dinero(datos.subtotal, datos.simboloMoneda)}</span>
                    </div>
                    <div className="ticket-fila">
                        <span>Descuentos</span>
                        <span>−{dinero(datos.descuentoTotal, datos.simboloMoneda)}</span>
                    </div>
                </>
            )}

            <div className="ticket-fila ticket-total">
                <span>TOTAL</span>
                <span>{dinero(datos.total, datos.simboloMoneda)}</span>
            </div>

            {datos.redondeo !== 0 && (
                <div className="ticket-fila ticket-tenue">
                    <span>Redondeo</span>
                    <span>
                        {datos.redondeo > 0 ? '+' : ''}
                        {dinero(datos.redondeo, datos.simboloMoneda)}
                    </span>
                </div>
            )}

            {datos.pagos.map((pago) => (
                <div key={pago.pagoVentaId} className="ticket-fila">
                    <span>{pago.medioPagoCodigo}</span>
                    <span>{dinero(pago.importe, datos.simboloMoneda)}</span>
                </div>
            ))}

            {datos.saldoACredito > 0 && (
                <div className="ticket-fila">
                    <span>SALDO A CRÉDITO</span>
                    <span>{dinero(datos.saldoACredito, datos.simboloMoneda)}</span>
                </div>
            )}

            {datos.ivaIncluido > 0 && (
                <div className="ticket-fila ticket-tenue">
                    <span>IVA incluido</span>
                    <span>{dinero(datos.ivaIncluido, datos.simboloMoneda)}</span>
                </div>
            )}

            <hr />

            <div className="ticket-centro ticket-tenue">
                {datos.mensaje && <div>{datos.mensaje}</div>}
                {/* Sin esto, alguien podria presentarlo como boleta. */}
                <div>Comprobante interno · no válido como boleta</div>
            </div>
        </div>
    )
}
